import {
  addDays as _addDays,
  addHours as _addHours,
  addMinutes as _addMinutes,
  addMonths as _addMonths,
  addSeconds as _addSeconds,
  addWeeks as _addWeeks,
  addYears as _addYears,
  compareAsc as _compareAsc,
  compareDesc as _compareDesc,
  differenceInDays as _diffInDays,
  differenceInMonths as _diffInMonths,
  differenceInWeeks as _diffInWeeks,
  eachDayOfInterval,
  eachWeekOfInterval,
  endOfDay as _endOfDay,
  endOfISOWeek as _endOfISOWeek,
  endOfMonth as _endOfMonth,
  endOfWeek,
  endOfYear as _endOfYear,
  format,
  formatISO,
  getDate,
  getHours as _getHours,
  getISODay,
  getMinutes as _getMinutes,
  getMonth,
  getYear,
  isAfter,
  isBefore,
  isEqual as _isEqual,
  isSameMonth,
  isSameWeek,
  startOfDay as _startOfDay,
  startOfISOWeek as _startOfISOWeek,
  startOfMonth as _startOfMonth,
  startOfWeek,
  startOfYear as _startOfYear,
  subDays as _subDays,
  subHours as _subHours,
  subMinutes as _subMinutes,
  subMonths as _subMonths,
  subSeconds as _subSeconds,
  subWeeks as _subWeeks,
  subYears as _subYears,
} from 'date-fns';

// Milliseconds per day
// export const _MS_PER_DAY = 1000 * 60 * 60 * 24
export const WEEKDAYS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// Date functions exported from date-fns
export const addYears = _addYears;
export const addMonths = _addMonths;
export const addWeeks = _addWeeks;
export const addDays = _addDays;
export const addHours = _addHours;
export const addMinutes = _addMinutes;
export const addSeconds = _addSeconds;
export const subYears = _subYears;
export const subMonths = _subMonths;
export const subWeeks = _subWeeks;
export const subDays = _subDays;
export const subHours = _subHours;
export const subMinutes = _subMinutes;
export const subSeconds = _subSeconds;
export const startOfDay = _startOfDay;
export const startOfISOWeek = _startOfISOWeek;
export const startOfMonth = _startOfMonth;
export const startOfYear = _startOfYear;
export const endOfDay = _endOfDay;
export const endOfISOWeek = _endOfISOWeek;
export const endOfMonth = _endOfMonth;
export const endOfYear = _endOfYear;
export const compareAsc = _compareAsc;
export const compareDesc = _compareDesc;
export const getHours = _getHours;
export const getMinutes = _getMinutes;
export const diffInMonths = _diffInMonths;
export const isEqual = _isEqual;

// Custom date and time functions
export function dateRange(
  startDate: Date,
  length = 7,
  inclusive = true
): Date[] {
  return [
    addDays(new Date(startDate.toString()), inclusive ? 0 : 1),
    addDays(new Date(startDate.toString()), length),
  ];
}

export function getDateWithCurrentTime(date: Date): Date {
  const now = new Date();

  now.setDate(date.getDate());
  now.setMonth(date.getMonth());
  now.setFullYear(date.getFullYear());

  return now;
}

export function toDates(dateStrings: string[]): Date[] {
  return dateStrings.map((dateString) => new Date(dateString));
}

export function toISOStrings(dates: Date[]): string[] {
  return dates.map((date) => date.toISOString());
}

export function toISODateString(date: Date): string {
  return formatISO(date, { representation: 'date' });
}

export function toISODateStrings(dates: Date[]): string[] {
  return dates.map((date) => formatISO(date, { representation: 'date' }));
}

export function toISOTimeString(date: Date): string {
  return formatISO(date, { representation: 'time' });
}

export function toISOTimeStrings(dates: Date[]): string[] {
  return dates.map((date) => formatISO(date, { representation: 'time' }));
}

export function diffInDays(startDate: Date, endDate: Date): number {
  // Returns the difference of the two dates.
  // The minus is necessary because (unintuitively!)
  // the original function expects the earlier date
  // to be passed as the second argument.
  return -_diffInDays(startOfDay(startDate), startOfDay(endDate));
}

export function weekRange(startDate = new Date(), weeks = 1): [string, string] {
  return [
    toISODateString(_startOfISOWeek(_subWeeks(_startOfDay(startDate), weeks))),
    toISODateString(
      _startOfISOWeek(_addWeeks(_startOfDay(startDate), weeks + 1))
    ),
  ];
}

export function monthRange(
  startDate = new Date(),
  months = 1
): [string, string] {
  return [
    toISODateString(_startOfMonth(_subMonths(_startOfDay(startDate), months))),
    toISODateString(
      _startOfMonth(_addMonths(_startOfDay(startDate), months + 1))
    ),
  ];
}

export function yearRange(startDate = new Date(), years = 1): [string, string] {
  return [
    toISODateString(_startOfYear(_subYears(_startOfDay(startDate), years))),
    toISODateString(_startOfYear(_addYears(_startOfDay(startDate), years + 1))),
  ];
}

export function prevWeekRange(startDate = new Date()): string[] {
  return weekRange(_subWeeks(startDate, 1));
}
export function nextWeekRange(startDate = new Date()): string[] {
  return weekRange(_addWeeks(startDate, 1));
}

export function prevMonthRange(startDate = new Date()): string[] {
  return monthRange(_subMonths(startDate, 1));
}
export function nextMonthRange(startDate = new Date()): string[] {
  return monthRange(_addMonths(startDate, 1));
}

export function prevYearRange(startDate = new Date()): string[] {
  return yearRange(_subYears(startDate, 1));
}

export function nextYearRange(startDate = new Date()): string[] {
  return yearRange(_addYears(startDate, 1));
}

export function monthAndYear(date: Date, months?: string[]): string[] {
  if (months) {
    return [months[getMonth(date)], getYear(date).toString()];
  }
  return [MONTHS[getMonth(date)], getYear(date).toString()];
}

export function weekDay(date: Date, weekDays?: string[]): string[] {
  if (weekDays) {
    return [weekDays[getISODay(date)], getDate(date).toString()];
  }
  return [WEEKDAYS[getISODay(date) - 1], getDate(date).toString()];
}

export function startsWithinWeeks(
  date: Date | string,
  edge: Date | string,
  weeks = 1
): boolean {
  return (
    _diffInWeeks(
      typeof date === 'string' ? new Date(date) : date,
      typeof edge === 'string' ? new Date(edge) : edge
    ) <= (weeks <= 0 ? 1 : weeks)
  );
}

export function endsWithinWeeks(
  date: Date | string,
  edge: Date | string,
  weeks = 1
): boolean {
  return (
    _diffInWeeks(
      typeof edge === 'string' ? new Date(edge) : edge,
      typeof date === 'string' ? new Date(date) : date
    ) <= (weeks <= 0 ? 1 : weeks)
  );
}

export function startsWithinMonths(
  date: Date | string,
  edge: Date | string,
  months = 1
): boolean {
  return (
    _diffInMonths(
      typeof date === 'string' ? new Date(date) : date,
      typeof edge === 'string' ? new Date(edge) : edge
    ) <= (months <= 0 ? 1 : months)
  );
}

export function endsWithinMonths(
  date: Date | string,
  edge: Date | string,
  months = 1
): boolean {
  return (
    _diffInMonths(
      typeof edge === 'string' ? new Date(edge) : edge,
      typeof date === 'string' ? new Date(date) : date
    ) <= (months <= 0 ? 1 : months)
  );
}

export const isNextWeek = (date: Date): boolean =>
  isSameWeek(date, addWeeks(new Date(), 1));

export const isPreviousWeek = (date: Date): boolean =>
  isSameWeek(date, addWeeks(new Date(), -1));

export function isWithinWeeks(
  interval: (Date | string)[],
  fetched: (Date | string)[],
  weeks = 1
): boolean {
  return (
    startsWithinWeeks(interval[0], fetched[0], weeks) ||
    endsWithinWeeks(interval[1], fetched[1], weeks)
  );
}
export function isWithinMonths(
  interval: (Date | string)[],
  fetched: (Date | string)[],
  months = 1
): boolean {
  return (
    startsWithinMonths(interval[0], fetched[0], months) ||
    endsWithinMonths(interval[1], fetched[1], months)
  );
}

export function timeAMPM(date: Date, ampm = false, minutes = true): string {
  return format(
    date,
    `${ampm ? 'hh' : 'h'}${minutes ? ':mm' : ''}${ampm ? ' a' : ''}`
  );
}

export const isBetween = (start: Date, end: Date) => (date: Date) =>
  isAfter(date, start) && isBefore(date, end);

/** Gets tomorrows date */
export const getTomorrow = () => {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  return tomorrow;
};

const getLastDays =
  (days: number) =>
  (date: Date): Date[] =>
    Array.from(Array(days).keys())
      .map((i) => {
        const day = new Date(date);
        day.setDate(day.getDate() - i);
        return day;
      })
      .reverse();

export const getLast7Days = getLastDays(7);

const getLastWeeks =
  (weeks: number) =>
  (date: Date): Date[] =>
    Array.from(Array(weeks).keys())
      .map((i) => {
        const day = new Date(date);
        day.setDate(day.getDate() - i * 7);
        return day;
      })
      .reverse();

export const getLast7Weeks = getLastWeeks(7);

const getLastMonths = (months: number) => (date: Date) =>
  Array.from(Array(months).keys())
    .map((i) => {
      const day = new Date(date);
      day.setMonth(day.getMonth() - i);
      return day;
    })
    .reverse();

export const getLast7Months = getLastMonths(7);

export const getWeeksOfMonth = (date: Date) => {
  const weeks = eachWeekOfInterval({
    start: startOfMonth(date),
    end: endOfMonth(date),
  });

  return weeks.map((week) => {
    const days = eachDayOfInterval({
      start: startOfWeek(week),
      end: endOfWeek(week),
    });

    return days.map((day) => (isSameMonth(day, date) ? day : undefined));
  });
};

export const getPaddedWeeksOfMonth = (date: Date) => {
  const current_month_weeks = getWeeksOfMonth(new Date(date));

  /** Middle weeks, these won't need padding */
  const middle_weeks = current_month_weeks.slice(1, -1);

  /** Strip undefined dates */
  let first_week = current_month_weeks[0].filter(Boolean);

  /** If the first week is not a full week, we need to pad it */
  if (first_week.length < 7) {
    /** First week is short, add padding */
    const first_day = first_week[0] as Date;

    /** Get the padding days */
    const padding_days = [...Array(7 - first_week.length)]
      .map((_, idx) => addDays(first_day, -(idx + 1)))
      .reverse();

    /** Add padding days to first week */
    first_week = [...padding_days, ...first_week];
  }

  /** Strip undefined dates */
  let last_week =
    current_month_weeks[current_month_weeks.length - 1].filter(Boolean);

  /** If the last week is not a full week, we need to pad it */
  if (last_week.length < 7) {
    /** Last week is short, add padding */
    const last_day = last_week[last_week.length - 1] as Date;

    /** Get the padding days */
    const padding_days = [...Array(7 - last_week.length)].map((_, idx) =>
      addDays(last_day, idx + 1)
    );

    /** Add padding days to last week */
    last_week = [...last_week, ...padding_days];
  }

  return [first_week, ...middle_weeks, last_week];
};

export const secondsToMMSS = (sec: number, separator = ':'): string =>
  `${Math.floor(sec / 60)}${separator}${('0' + (sec % 60)).slice(-2)}`;

export const durationToSeconds = (unit: string, duration: number): number => {
  let durationAsSeconds: number = duration;
  switch (unit) {
    case 'minute':
      durationAsSeconds = duration * 60;
      break;
    case 'hour':
      durationAsSeconds = duration * 60 * 60;
      break;
  }

  return durationAsSeconds;
};
